import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';

export type WorkspaceRole = 'admin' | 'editor' | 'viewer';
export type ApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'active';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description?: string;
  ownerId: string;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  role: WorkspaceRole;
  invitedBy?: string;
  invitedAt: string;
  joinedAt?: string;
  email?: string;
}

export interface WorkspaceInvitation {
  id: string;
  workspaceId: string;
  email: string;
  role: WorkspaceRole;
  token: string;
  invitedBy: string;
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

export interface WorkspaceFolder {
  id: string;
  workspaceId: string;
  name: string;
  color: string;
  parentId?: string;
  createdBy: string;
  createdAt: string;
}

function mapDbToWorkspace(db: Record<string, unknown>): Workspace {
  return {
    id: db.id as string,
    name: db.name as string,
    slug: db.slug as string,
    description: db.description as string | undefined,
    ownerId: db.owner_id as string,
    logoUrl: db.logo_url as string | undefined,
    createdAt: db.created_at as string,
    updatedAt: db.updated_at as string,
  };
}

function mapDbToMember(db: Record<string, unknown>): WorkspaceMember {
  return {
    id: db.id as string,
    workspaceId: db.workspace_id as string,
    userId: db.user_id as string,
    role: db.role as WorkspaceRole,
    invitedBy: db.invited_by as string | undefined,
    invitedAt: db.invited_at as string,
    joinedAt: db.joined_at as string | undefined,
  };
}

function mapDbToInvitation(db: Record<string, unknown>): WorkspaceInvitation {
  return {
    id: db.id as string,
    workspaceId: db.workspace_id as string,
    email: db.email as string,
    role: db.role as WorkspaceRole,
    token: db.token as string,
    invitedBy: db.invited_by as string,
    expiresAt: db.expires_at as string,
    acceptedAt: db.accepted_at as string | undefined,
    createdAt: db.created_at as string,
  };
}

function mapDbToFolder(db: Record<string, unknown>): WorkspaceFolder {
  return {
    id: db.id as string,
    workspaceId: db.workspace_id as string,
    name: db.name as string,
    color: db.color as string,
    parentId: db.parent_id as string | undefined,
    createdBy: db.created_by as string,
    createdAt: db.created_at as string,
  };
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50) + '-' + Math.random().toString(36).slice(2, 8);
}

export function useWorkspaces() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['workspaces', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(d => mapDbToWorkspace(d as Record<string, unknown>));
    },
    enabled: !!user,
  });

  const createWorkspace = useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('workspaces')
        .insert({
          name,
          slug: generateSlug(name),
          description,
          owner_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return mapDbToWorkspace(data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace created!');
    },
    onError: () => {
      toast.error('Failed to create workspace');
    },
  });

  const updateWorkspace = useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name?: string; description?: string }) => {
      const updates: Record<string, unknown> = {};
      if (name !== undefined) updates.name = name;
      if (description !== undefined) updates.description = description;

      const { error } = await supabase
        .from('workspaces')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace updated!');
    },
  });

  const deleteWorkspace = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('workspaces')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace deleted');
    },
  });

  return {
    workspaces,
    isLoading,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  };
}

export function useWorkspaceMembers(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: members, isLoading } = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('joined_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(d => mapDbToMember(d as Record<string, unknown>));
    },
    enabled: !!workspaceId,
  });

  const { data: invitations } = useQuery({
    queryKey: ['workspace-invitations', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('workspace_id', workspaceId)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(d => mapDbToInvitation(d as Record<string, unknown>));
    },
    enabled: !!workspaceId,
  });

  const inviteMember = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: WorkspaceRole }) => {
      if (!workspaceId || !user) throw new Error('Missing context');

      const { data, error } = await supabase
        .from('workspace_invitations')
        .insert({
          workspace_id: workspaceId,
          email: email.toLowerCase(),
          role,
          invited_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return mapDbToInvitation(data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] });
      toast.success('Invitation sent!');
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast.error('This email has already been invited');
      } else {
        toast.error('Failed to send invitation');
      }
    },
  });

  const updateMemberRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: WorkspaceRole }) => {
      const { error } = await supabase
        .from('workspace_members')
        .update({ role })
        .eq('id', memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      toast.success('Role updated');
    },
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('workspace_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      toast.success('Member removed');
    },
  });

  const cancelInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('workspace_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] });
      toast.success('Invitation cancelled');
    },
  });

  return {
    members,
    invitations,
    isLoading,
    inviteMember,
    updateMemberRole,
    removeMember,
    cancelInvitation,
  };
}

export function useWorkspaceFolders(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: folders, isLoading } = useQuery({
    queryKey: ['workspace-folders', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from('workspace_folders')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('name');

      if (error) throw error;
      return (data || []).map(d => mapDbToFolder(d as Record<string, unknown>));
    },
    enabled: !!workspaceId,
  });

  const createFolder = useMutation({
    mutationFn: async ({ name, color, parentId }: { name: string; color?: string; parentId?: string }) => {
      if (!workspaceId || !user) throw new Error('Missing context');

      const { data, error } = await supabase
        .from('workspace_folders')
        .insert({
          workspace_id: workspaceId,
          name,
          color: color || '#6366f1',
          parent_id: parentId,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return mapDbToFolder(data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-folders', workspaceId] });
      toast.success('Folder created');
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async (folderId: string) => {
      const { error } = await supabase
        .from('workspace_folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-folders', workspaceId] });
      toast.success('Folder deleted');
    },
  });

  return {
    folders,
    isLoading,
    createFolder,
    deleteFolder,
  };
}

export function useMyWorkspaceRole(workspaceId: string | undefined) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ['my-workspace-role', workspaceId, user?.id],
    queryFn: async (): Promise<WorkspaceRole | null> => {
      if (!workspaceId || !user) return null;

      // Check if owner
      const { data: workspace } = await supabase
        .from('workspaces')
        .select('owner_id')
        .eq('id', workspaceId)
        .single();

      if (workspace?.owner_id === user.id) return 'admin';

      // Check membership
      const { data: member } = await supabase
        .from('workspace_members')
        .select('role')
        .eq('workspace_id', workspaceId)
        .eq('user_id', user.id)
        .maybeSingle();

      return (member?.role as WorkspaceRole) || null;
    },
    enabled: !!workspaceId && !!user,
  });
}
